/**
 * Hope — HepCURE's voice-first HCV assistant (Conversational build).
 *
 * PIPELINE (all servers — no browser Web Speech API):
 *   Voice in   :  getUserMedia → MediaRecorder → Whisper (POST /api/hope/stt)
 *                 + real-time AnalyserNode VAD for silence-end + barge-in
 *   Brain      :  POST /api/hope/chat  →  Claude Sonnet 4.6 (HCV-only)
 *   Voice out  :  POST /api/hope/tts   →  ElevenLabs "Sara Sloane" MP3
 *
 * CONVERSATIONAL BEHAVIOUR:
 *   - Auto voice-activity detection stops the mic ~1.5 s after you finish
 *     speaking. No second tap.
 *   - After Hope finishes speaking, the mic re-opens automatically for
 *     the next turn. Real back-and-forth.
 *   - Barge-in: talk over Hope and she stops mid-sentence, then listens.
 *   - Live level meter on the mic button so you can *see* she's hearing you.
 *
 *   User can force-stop the whole thing anytime (tap red button, or close).
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { Mic, X, Keyboard, MessageCircle, Send, Loader2 } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const HOPE_CHAT_API = `${BACKEND_URL}/api/hope/chat`;
const HOPE_STT_API  = `${BACKEND_URL}/api/hope/stt`;
const HOPE_TTS_API  = `${BACKEND_URL}/api/hope/tts`;

const AVATAR_IMAGE  = "/hope/hope-poster.jpg";  // still portrait of the outreach worker

// Hope is a preview-only feature for now. She renders only on preview /
// dev / localhost hosts. Any production hostname (hepcure.ca, www., or a
// deployed Emergent subdomain without ".preview." in it) will silently
// not mount her. This is intentional — the user can push other changes
// to production without shipping Hope.
//
// IMPORTANT: only ".preview.*" subdomains and local dev qualify. Do NOT
// broaden this to "*.emergentagent.com" — the deployed production build
// also lives on an emergentagent subdomain and would leak Hope there.
export const isPreviewHost = () => {
  if (typeof window === "undefined") return false;
  const h = window.location.hostname.toLowerCase();
  return (
    h.endsWith(".preview.emergentagent.com") ||
    h.endsWith(".preview.emergent.host") ||
    h === "localhost" ||
    h === "127.0.0.1" ||
    h.endsWith(".local")
  );
};

const GREETING = (
  "Hi there — I'm Hope. I can answer questions about Hepatitis C, " +
  "screening, treatment, or how to reach the HepCURE mobile team. " +
  "Just start speaking whenever you're ready."
);

// VAD tuning
const VAD_START_THRESHOLD = 0.030;   // RMS above this = user is speaking
const VAD_STOP_THRESHOLD  = 0.020;   // RMS below this = silence
const SILENCE_END_MS      = 1400;    // stop recording after this much trailing silence
const MIN_SPEECH_MS       = 400;     // ignore ultra-short blips (mic click / bump)
const MAX_TURN_MS         = 30000;   // hard cap per user turn
const BARGE_IN_THRESHOLD  = 0.045;   // slightly stricter to avoid Hope's own audio triggering it
const BARGE_IN_MS         = 250;     // must exceed threshold this long
const AUTOSTART_DELAY_MS  = 350;     // small pause after Hope finishes before next mic open
const AUTOSTART_IDLE_MS   = 15000;   // if no speech detected in auto-open, close gracefully

const MediaRecorderAPI = typeof window !== "undefined" ? window.MediaRecorder : null;
const AudioCtx =
  typeof window !== "undefined"
    ? (window.AudioContext || window.webkitAudioContext)
    : null;

const pickRecorderMime = () => {
  if (!MediaRecorderAPI) return "";
  const candidates = [
    "audio/webm;codecs=opus",
    "audio/webm",
    "audio/mp4;codecs=mp4a.40.2",
    "audio/mp4",
    "audio/ogg;codecs=opus",
  ];
  for (const t of candidates) {
    try {
      if (MediaRecorderAPI.isTypeSupported && MediaRecorderAPI.isTypeSupported(t)) return t;
    } catch (_) { /* noop */ }
  }
  return "";
};

const AvatarImage = ({ size = "circle" }) => {
  const cls = size === "launcher"
    ? "absolute inset-0 h-full w-full object-cover"
    : "absolute inset-0 h-full w-full object-cover";
  return (
    <img
      className={cls}
      src={AVATAR_IMAGE}
      alt="HepCURE outreach worker"
      draggable={false}
      style={{ objectPosition: "75% 55%" }}
    />
  );
};

export const HopeWidget = () => {
  // UI state
  const [open, setOpen] = useState(false);
  // Legal disclaimer must be accepted before the chat interface unlocks.
  // On production: persists per browser (localStorage) so returning users
  // skip it after their first acceptance.
  // On preview: never persists — you always see it on every open so
  // it's easy to test the disclaimer flow repeatedly.
  const isPreviewEnv = typeof window !== "undefined" &&
    /preview\.emergentagent\.com$/i.test(window.location.hostname);
  const [hasAgreedToDisclaimer, setHasAgreedToDisclaimer] = useState(() => {
    if (isPreviewEnv) return false;
    try {
      return localStorage.getItem("hope_disclaimer_agreed_v1") === "true";
    } catch {
      return false;
    }
  });
  const [disclaimerChecked, setDisclaimerChecked] = useState(false);
  const [mode, setMode] = useState("idle"); // idle | listening | thinking | speaking
  const [micLevel, setMicLevel] = useState(0); // 0-1 for the volume ring
  const [showTypeInstead, setShowTypeInstead] = useState(false);
  const [typedInput, setTypedInput] = useState("");
  const [error, setError] = useState("");
  const [permissionOk, setPermissionOk] = useState(null); // null=unknown, true=granted, false=denied

  // Conversation
  const [history, setHistory] = useState([]);
  const historyRef = useRef(history);
  useEffect(() => { historyRef.current = history; }, [history]);

  // Refs — audio graph
  const streamRef = useRef(null);
  const audioCtxRef = useRef(null);
  const analyserRef = useRef(null);
  const analyserBufRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const rafRef = useRef(null);
  const speechStartedAtRef = useRef(0);
  const lastLoudAtRef = useRef(0);
  const bargeStartedAtRef = useRef(0);
  const turnStartedAtRef = useRef(0);
  const listenerIdleTimeoutRef = useRef(null);

  // Refs — playback
  const audioElRef = useRef(null);
  const blobUrlRef = useRef(null);
  const conversationalRef = useRef(true); // auto-continue after Hope speaks
  const sessionIdRef = useRef(null);
  const stoppingRef = useRef(false);

  // ----------- MIC / VAD ENGINE -----------
  const teardownAudioGraph = useCallback(() => {
    if (rafRef.current) { cancelAnimationFrame(rafRef.current); rafRef.current = null; }
    if (listenerIdleTimeoutRef.current) {
      clearTimeout(listenerIdleTimeoutRef.current); listenerIdleTimeoutRef.current = null;
    }
    try { recorderRef.current?.stream?.getTracks?.().forEach((t) => t.stop()); } catch (_) { /* noop */ }
    try { streamRef.current?.getTracks?.().forEach((t) => t.stop()); } catch (_) { /* noop */ }
    streamRef.current = null;
    recorderRef.current = null;
    try { audioCtxRef.current?.close?.(); } catch (_) { /* noop */ }
    audioCtxRef.current = null;
    analyserRef.current = null;
    analyserBufRef.current = null;
  }, []);

  const stopPlayback = useCallback(() => {
    try {
      const a = audioElRef.current;
      if (a) { a.pause(); a.src = ""; }
    } catch (_) { /* noop */ }
    if (blobUrlRef.current) {
      try { URL.revokeObjectURL(blobUrlRef.current); } catch (_) { /* noop */ }
      blobUrlRef.current = null;
    }
  }, []);

  // Ensure mic + audio graph are up. Returns true on success.
  const ensureMic = useCallback(async () => {
    if (streamRef.current && audioCtxRef.current) return true;
    if (!navigator?.mediaDevices?.getUserMedia || !MediaRecorderAPI || !AudioCtx) {
      setError("Voice isn't supported in this browser — use Type instead.");
      setShowTypeInstead(true);
      return false;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          channelCount: 1,
        },
      });
      streamRef.current = stream;
      setPermissionOk(true);

      const ctx = new AudioCtx();
      audioCtxRef.current = ctx;
      const src = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 1024;
      analyser.smoothingTimeConstant = 0.6;
      analyserBufRef.current = new Float32Array(analyser.fftSize);
      src.connect(analyser);
      analyserRef.current = analyser;
      return true;
    } catch (e) {
      const name = e?.name || "";
      if (name === "NotAllowedError" || name === "SecurityError") {
        setPermissionOk(false);
        setError("Microphone access denied. Please enable it in your browser settings.");
      } else {
        setError(`Microphone error: ${name || e?.message || "unknown"}`);
      }
      return false;
    }
  }, []);

  const readRms = useCallback(() => {
    const a = analyserRef.current;
    const buf = analyserBufRef.current;
    if (!a || !buf) return 0;
    a.getFloatTimeDomainData(buf);
    let sum = 0;
    for (let i = 0; i < buf.length; i += 1) sum += buf[i] * buf[i];
    return Math.sqrt(sum / buf.length);
  }, []);

  // ---- LISTENING LOOP ----
  const stopListeningAndSend = useCallback(async () => {
    if (stoppingRef.current) return;
    stoppingRef.current = true;
    if (rafRef.current) { cancelAnimationFrame(rafRef.current); rafRef.current = null; }
    if (listenerIdleTimeoutRef.current) {
      clearTimeout(listenerIdleTimeoutRef.current); listenerIdleTimeoutRef.current = null;
    }
    const rec = recorderRef.current;
    if (!rec || rec.state === "inactive") {
      stoppingRef.current = false;
      setMode("idle");
      return;
    }
    try { rec.stop(); } catch (_) { /* noop */ }
  }, []);

  const startListening = useCallback(async (opts = { auto: false }) => {
    setError("");
    if (mode === "speaking") stopPlayback();
    const ok = await ensureMic();
    if (!ok) { setMode("idle"); return; }

    // Start MediaRecorder
    const mime = pickRecorderMime();
    let rec;
    try {
      rec = new MediaRecorderAPI(streamRef.current, mime ? { mimeType: mime } : undefined);
    } catch (e) {
      setError(`Recorder init failed: ${e?.message || e}`);
      setMode("idle");
      return;
    }
    chunksRef.current = [];
    rec.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunksRef.current.push(e.data); };
    rec.onstop = async () => {
      stoppingRef.current = false;
      const blob = new Blob(chunksRef.current, { type: rec.mimeType || "audio/webm" });
      chunksRef.current = [];
      // Below threshold? Say nothing.
      if (blob.size < 1200 || (Date.now() - speechStartedAtRef.current) < MIN_SPEECH_MS) {
        // If the user tapped mic and never spoke, gracefully idle.
        if (opts.auto) {
          setMode("idle");
        } else {
          setError("I didn't catch anything — try again a little louder.");
          setMode("idle");
        }
        return;
      }
      setMode("thinking");
      // Send to Whisper
      try {
        const form = new FormData();
        const filename = (rec.mimeType || "").includes("mp4") ? "clip.mp4" : "clip.webm";
        form.append("audio", blob, filename);
        const { data } = await axios.post(HOPE_STT_API, form, { timeout: 45000 });
        const transcript = (data?.transcript || "").trim();
        if (!transcript) {
          setError("I couldn't understand that — try speaking a little clearer.");
          setMode("idle");
          return;
        }
        // Route to chat + speak
        await sendAndSpeak(transcript);
      } catch (e) {
        const msg = e?.response?.data?.detail || e?.message || "Transcription failed.";
        setError(String(msg));
        setMode("idle");
      }
    };
    recorderRef.current = rec;

    speechStartedAtRef.current = 0;
    lastLoudAtRef.current = 0;
    turnStartedAtRef.current = Date.now();
    stoppingRef.current = false;
    rec.start(200); // slice every 200ms so the recorder buffers cleanly

    setMode("listening");

    // If auto-mode and user says nothing at all, gracefully idle after N seconds.
    if (opts.auto) {
      listenerIdleTimeoutRef.current = setTimeout(() => {
        // Only tear down if we never detected speech
        if (speechStartedAtRef.current === 0) {
          stopListeningAndSend();
        }
      }, AUTOSTART_IDLE_MS);
    }

    // VAD loop
    const loop = () => {
      if (!recorderRef.current || recorderRef.current.state === "inactive") return;
      const rms = readRms();
      setMicLevel(Math.min(1, rms * 12));
      const now = Date.now();

      if (rms >= VAD_START_THRESHOLD) {
        if (speechStartedAtRef.current === 0) speechStartedAtRef.current = now;
        lastLoudAtRef.current = now;
      }

      const spoke = speechStartedAtRef.current !== 0;
      const silentFor = spoke ? now - lastLoudAtRef.current : 0;

      // End conditions:
      if (spoke && silentFor > SILENCE_END_MS) {
        stopListeningAndSend();
        return;
      }
      if (now - turnStartedAtRef.current > MAX_TURN_MS) {
        stopListeningAndSend();
        return;
      }
      rafRef.current = requestAnimationFrame(loop);
    };
    rafRef.current = requestAnimationFrame(loop);
  }, [mode, stopPlayback, ensureMic, readRms, stopListeningAndSend]);

  // ---- BARGE-IN LOOP (runs while Hope is speaking) ----
  const startBargeInWatcher = useCallback(() => {
    if (!analyserRef.current) return;
    bargeStartedAtRef.current = 0;
    const loop = () => {
      if (mode !== "speaking") return;
      const rms = readRms();
      const now = Date.now();
      if (rms >= BARGE_IN_THRESHOLD) {
        if (bargeStartedAtRef.current === 0) bargeStartedAtRef.current = now;
        if (now - bargeStartedAtRef.current > BARGE_IN_MS) {
          // User is interrupting — stop Hope and start listening
          stopPlayback();
          setMode("idle");
          startListening({ auto: true });
          return;
        }
      } else {
        bargeStartedAtRef.current = 0;
      }
      rafRef.current = requestAnimationFrame(loop);
    };
    rafRef.current = requestAnimationFrame(loop);
  }, [mode, readRms, stopPlayback, startListening]);

  // ---- SPEAK (ElevenLabs → play → auto-continue) ----
  const speak = useCallback(async (text) => {
    if (!text) return;
    stopPlayback();
    try {
      setMode("speaking");
      const resp = await axios.post(HOPE_TTS_API, { text }, { responseType: "blob", timeout: 30000 });
      const blob = resp.data;
      if (!blob || blob.size < 500) throw new Error("empty audio");
      const url = URL.createObjectURL(blob);
      blobUrlRef.current = url;
      const audio = audioElRef.current || new Audio();
      audioElRef.current = audio;
      audio.src = url;

      // Kick barge-in watcher after a short delay (avoid picking up own speaker
      // if headphones aren't used — echoCancellation should handle most cases).
      setTimeout(() => { if (audioCtxRef.current) startBargeInWatcher(); }, 400);

      await new Promise((resolve) => {
        const done = () => { audio.onended = null; audio.onerror = null; resolve(); };
        audio.onended = done;
        audio.onerror = done;
        const p = audio.play();
        if (p?.catch) p.catch(() => done());
      });
    } catch (e) {
      console.warn("[Hope] TTS failed:", e?.message || e);
    } finally {
      // Auto-continue: reopen mic for the next turn
      if (conversationalRef.current && !stoppingRef.current && open) {
        setMode("idle");
        setTimeout(() => {
          // only auto-listen if still open + not already listening
          if (open && mode !== "listening") startListening({ auto: true });
        }, AUTOSTART_DELAY_MS);
      } else {
        setMode("idle");
      }
    }
  }, [open, mode, startBargeInWatcher, startListening, stopPlayback]);

  // ---- CHAT + SPEAK ----
  const sendAndSpeak = useCallback(async (userText) => {
    if (!userText?.trim()) return;
    const clean = userText.trim();
    const nextHistory = [...historyRef.current, { role: "user", content: clean }];
    setHistory(nextHistory);
    setMode("thinking");
    try {
      const { data } = await axios.post(
        HOPE_CHAT_API,
        { message: clean, history: historyRef.current, session_id: sessionIdRef.current },
        { timeout: 30000 },
      );
      sessionIdRef.current = data.session_id;
      const reply = (data.reply || "").trim();
      setHistory([...nextHistory, { role: "assistant", content: reply }]);
      await speak(reply);
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Something went wrong.";
      setError(String(msg));
      setMode("idle");
    }
  }, [speak]);

  // ---- UI HANDLERS ----
  const primaryButton = useCallback(async () => {
    if (mode === "listening") { stopListeningAndSend(); return; }
    if (mode === "speaking")  {
      stopPlayback();
      setMode("idle");
      setTimeout(() => startListening({ auto: false }), 50);
      return;
    }
    if (mode === "thinking") return;
    // idle → open mic
    await startListening({ auto: false });
  }, [mode, stopListeningAndSend, stopPlayback, startListening]);

  const handleClose = useCallback(() => {
    conversationalRef.current = false;
    stopPlayback();
    stopListeningAndSend();
    teardownAudioGraph();
    setMode("idle");
    setOpen(false);
    // On preview, reset the disclaimer so it re-appears every time — makes
    // the flow easy to test. Production keeps the user's saved acceptance.
    if (isPreviewEnv) {
      setHasAgreedToDisclaimer(false);
      setDisclaimerChecked(false);
      openedOnceRef.current = false;
      greetedRef.current = false;
    }
    // Reset conversational for next open
    setTimeout(() => { conversationalRef.current = true; }, 0);
  }, [stopPlayback, stopListeningAndSend, teardownAudioGraph, isPreviewEnv]);

  // Hope stays SILENT on open — she only speaks in response to a user
  // action (tap the mic, submit typed text). The panel still shows the
  // written disclaimer + greeting so the user has full context, but no
  // audio auto-plays. This was an explicit product ask: "she should be
  // quiet on preview until she is called for assistance."
  const openedOnceRef = useRef(false);
  const greetedRef = useRef(false);
  const DISCLAIMER_SPOKEN =
    "Before we begin, a quick note. I share general information about " +
    "Hepatitis C. What I say is not medical advice and is not a substitute " +
    "for a conversation with your doctor, nurse, or pharmacist. " +
    "HepCURE is not responsible for decisions made based on my replies. " +
    "Please review the disclaimer on your screen and tap to agree before we start.";

  // Cleanup on unmount
  useEffect(() => () => {
    stopPlayback();
    teardownAudioGraph();
  }, [stopPlayback, teardownAudioGraph]);

  // Typed submit
  const onTypedSubmit = useCallback((e) => {
    e?.preventDefault?.();
    const t = typedInput.trim();
    if (!t) return;
    setTypedInput("");
    sendAndSpeak(t);
  }, [typedInput, sendAndSpeak]);

  const statusLabel = () => {
    if (mode === "listening") return "Listening…";
    if (mode === "thinking")  return "Thinking…";
    if (mode === "speaking")  return "Speaking…";
    return "Tap the mic to talk";
  };

  const ringClasses = () => {
    const base = "absolute inset-0 rounded-full border-4 transition-all";
    if (mode === "listening") return `${base} border-[#6BA292] animate-ping-slow`;
    if (mode === "speaking")  return `${base} border-[#6BA292] animate-pulse`;
    if (mode === "thinking")  return `${base} border-[#1F4E79]/40`;
    return `${base} border-[#6BA292]/40`;
  };

  const micScale = mode === "listening" ? 1 + micLevel * 0.25 : 1;

  return (
    <>
      {/* Launcher */}
      {!open && (
        <div
          className="fixed bottom-6 right-6 z-[70] flex items-center gap-3 select-none"
          data-testid="hope-launcher"
          data-hope-widget
        >
          <div
            className="hidden sm:flex items-center gap-2 bg-[#1F4E79] text-white px-4 py-2 rounded-full shadow-lg text-sm font-medium"
            data-testid="hope-launcher-tooltip"
          >
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#6BA292] opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-[#6BA292]"></span>
            </span>
            Ask Hope — your HepCURE guide
          </div>
          <button
            type="button"
            onClick={() => setOpen(true)}
            className="relative h-16 w-16 sm:h-[72px] sm:w-[72px] rounded-full overflow-hidden shadow-xl border-2 border-white/80 ring-4 ring-[#6BA292]/30 hover:ring-[#6BA292]/60 transition"
            aria-label="Open Hope, HepCURE's HCV assistant"
            data-testid="hope-launcher-button"
          >
            <AvatarImage size="launcher" />
          </button>
        </div>
      )}

      {/* Panel */}
      {open && (
        <div
          className="fixed bottom-4 right-4 z-[80] w-[calc(100vw-2rem)] sm:w-[380px] max-h-[calc(100vh-2rem)] flex flex-col rounded-2xl bg-white shadow-2xl border border-black/5 overflow-hidden"
          data-testid="hope-panel"
        >
          <div className="flex items-center gap-3 px-4 py-3 border-b border-black/5">
            <div className="h-8 w-8 rounded-full bg-[#1F4E79] text-white text-xs font-bold flex items-center justify-center">Hep</div>
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-[#1F4E79] text-sm">Hope — HepCURE Assistant</div>
              <div className="text-xs text-[#6BA292] flex items-center gap-1">
                <span className="h-1.5 w-1.5 rounded-full bg-[#6BA292] animate-pulse" />
                Online · HCV information only
              </div>
            </div>
            <button
              type="button"
              onClick={handleClose}
              className="h-8 w-8 rounded-full hover:bg-black/5 flex items-center justify-center text-gray-500"
              aria-label="Close Hope"
              data-testid="hope-close-button"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {!hasAgreedToDisclaimer ? (
            /* First-run legal disclaimer — user must tick the box before
               the chat interface unlocks. Choice is persisted per browser
               via localStorage so returning users skip this screen. */
            <div className="flex flex-col px-5 pt-4 pb-5" data-testid="hope-disclaimer">
              <p className="text-sm text-gray-700 leading-relaxed text-justify">
                Hope shares <strong>general information</strong> about
                Hepatitis&nbsp;C. It is <strong>not medical advice</strong>{" "}
                and is not a substitute for a conversation with a doctor,
                nurse, or pharmacist. For questions about your own health,
                please speak with a qualified healthcare provider.
                HepCURE is not responsible for decisions made based on
                Hope&apos;s replies.
              </p>
              <label
                className="mt-4 flex items-start gap-2.5 cursor-pointer select-none text-sm text-gray-800"
                data-testid="hope-disclaimer-consent-label"
              >
                <input
                  type="checkbox"
                  checked={disclaimerChecked}
                  onChange={(e) => setDisclaimerChecked(e.target.checked)}
                  className="mt-0.5 h-4 w-4 accent-[#1F4E79] flex-shrink-0"
                  data-testid="hope-disclaimer-consent-checkbox"
                />
                <span>I understand and agree.</span>
              </label>
              <button
                type="button"
                disabled={!disclaimerChecked}
                onClick={() => {
                  if (!isPreviewEnv) {
                    try {
                      localStorage.setItem("hope_disclaimer_agreed_v1", "true");
                    } catch { /* storage may be blocked — proceed anyway */ }
                  }
                  setHasAgreedToDisclaimer(true);
                }}
                data-testid="hope-disclaimer-continue"
                className="mt-4 w-full h-10 rounded-full bg-[#1F4E79] text-white text-sm font-semibold disabled:opacity-40 disabled:cursor-not-allowed hover:bg-[#183d5f] transition"
              >
                Start chat
              </button>
            </div>
          ) : (
          <>
          <div className="flex flex-col items-center pt-5 pb-2 px-4">
            <div className="relative h-40 w-40 rounded-full overflow-hidden">
              <AvatarImage size="panel" />
              <div className={ringClasses()} />
              {mode === "listening" && (
                <div
                  className="absolute inset-0 rounded-full border-4 border-[#6BA292]/80 pointer-events-none"
                  style={{ transform: `scale(${1 + micLevel * 0.15})`, transition: "transform 60ms linear" }}
                />
              )}
            </div>
            <div className="mt-3 text-sm text-gray-600 flex items-center gap-1.5" data-testid="hope-status">
              {mode === "thinking" && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              {statusLabel()}
            </div>
          </div>

          <div className="flex-1 overflow-y-auto px-4 py-2 space-y-2 min-h-[80px] max-h-[220px]" data-testid="hope-transcript">
            {history.length === 0 && (
              <div className="bg-[#6BA292]/10 rounded-lg px-3 py-2 text-sm text-[#1F4E79]">
                {GREETING}
              </div>
            )}
            {history.map((turn, i) => (
              <div
                key={i}
                className={
                  turn.role === "user"
                    ? "bg-gray-100 rounded-lg px-3 py-2 text-sm text-gray-800 ml-6"
                    : "bg-[#6BA292]/10 rounded-lg px-3 py-2 text-sm text-[#1F4E79] mr-6"
                }
                data-testid={`hope-turn-${turn.role}`}
              >
                <div className="text-[10px] font-semibold uppercase tracking-wide opacity-60 mb-0.5">
                  {turn.role === "user" ? "You" : "Hope"}
                </div>
                {turn.content}
              </div>
            ))}
            {error && (
              <div className="bg-red-50 rounded-lg px-3 py-2 text-xs text-red-700" data-testid="hope-error">
                {error}
              </div>
            )}
            {permissionOk === false && (
              <div className="bg-amber-50 rounded-lg px-3 py-2 text-xs text-amber-800">
                Hope needs microphone permission. Enable it and try again — or use Type instead.
              </div>
            )}
          </div>

          {!showTypeInstead ? (
            <div className="border-t border-black/5 px-4 py-3 flex flex-col items-center gap-2">
              <button
                type="button"
                onClick={primaryButton}
                disabled={mode === "thinking"}
                className={
                  "relative h-16 w-16 rounded-full flex items-center justify-center shadow-md transition " +
                  (mode === "listening"
                    ? "bg-red-500 hover:bg-red-600 text-white"
                    : mode === "thinking"
                    ? "bg-gray-300 text-white cursor-not-allowed"
                    : "bg-[#6BA292] hover:bg-[#5a8f80] text-white")
                }
                aria-label={mode === "listening" ? "Stop listening" : "Start speaking"}
                data-testid="hope-mic-button"
                style={{ transform: `scale(${micScale})`, transition: "transform 80ms linear" }}
              >
                <Mic className="h-7 w-7" />
              </button>
              <div className="flex items-center gap-3 text-[11px] text-gray-500">
                <button
                  type="button"
                  onClick={() => setShowTypeInstead(true)}
                  className="hover:text-[#1F4E79] flex items-center gap-1"
                  data-testid="hope-type-instead"
                >
                  <Keyboard className="h-3 w-3" /> Type instead
                </button>
                {mode === "speaking" && (
                  <button
                    type="button"
                    onClick={() => { stopPlayback(); setMode("idle"); }}
                    className="hover:text-red-600"
                    data-testid="hope-stop-speaking"
                  >
                    Stop
                  </button>
                )}
                {mode === "listening" && (
                  <span className="text-[11px] text-[#6BA292] font-medium">
                    Speak now — I&apos;ll stop automatically
                  </span>
                )}
              </div>
            </div>
          ) : (
            <form
              onSubmit={onTypedSubmit}
              className="border-t border-black/5 px-3 py-3 flex items-center gap-2"
              data-testid="hope-typed-form"
            >
              <input
                type="text"
                value={typedInput}
                onChange={(e) => setTypedInput(e.target.value)}
                placeholder="Ask about Hep C…"
                className="flex-1 rounded-full border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:border-[#6BA292]"
                data-testid="hope-typed-input"
                autoFocus
              />
              <button
                type="submit"
                disabled={!typedInput.trim() || mode === "thinking"}
                className="h-9 w-9 rounded-full bg-[#6BA292] hover:bg-[#5a8f80] text-white flex items-center justify-center disabled:opacity-40"
                data-testid="hope-typed-submit"
                aria-label="Send"
              >
                {mode === "thinking" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              </button>
              <button
                type="button"
                onClick={() => setShowTypeInstead(false)}
                className="text-xs text-gray-500 hover:text-[#1F4E79]"
                data-testid="hope-back-to-voice"
              >
                <MessageCircle className="h-4 w-4" />
              </button>
            </form>
          )}
          </>
          )}
        </div>
      )}

      <style>{`
        @keyframes hope-ping-slow { 0%{transform:scale(1);opacity:.9} 80%,100%{transform:scale(1.35);opacity:0} }
        .animate-ping-slow { animation: hope-ping-slow 1.6s cubic-bezier(0,0,.2,1) infinite; }
        [data-testid="hope-launcher-button"] video,
        [data-testid="hope-panel"] video { filter: saturate(1.05) contrast(1.02); }
      `}</style>
    </>
  );
};

export default HopeWidget;
